import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { DollarSign, TrendingUp, TrendingDown, ArrowLeftRight, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Check, FileText, ChevronsUpDown, Filter } from 'lucide-react';

const formatBRL = (v) => (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

function formatMesLabel(mes) {
  const [y, m] = mes.split('-');
  const meses = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
  return `${meses[parseInt(m) - 1]} ${y}`;
}
function moveMes(mes, delta) {
  const [y, m] = mes.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const StatusBadge = ({ status }) => {
  const map = { pago: 'bg-green-100 text-green-700', parcial: 'bg-amber-100 text-amber-700', pendente: 'bg-red-100 text-red-600' };
  const label = { pago: 'Pago', parcial: 'Parcial', pendente: 'Pendente' };
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${map[status] || 'bg-gray-100 text-gray-500'}`}>{label[status] || status}</span>;
};

// Ícone de ordenação para o cabeçalho
const SortIcon = ({ col, sortCol, sortDir }) => {
  if (sortCol !== col) return <ChevronsUpDown className="w-3 h-3 text-gray-300 ml-1 inline" />;
  return sortDir === 'asc'
    ? <ChevronUp className="w-3 h-3 text-blue-500 ml-1 inline" />
    : <ChevronDown className="w-3 h-3 text-blue-500 ml-1 inline" />;
};

// Th clicável
const Th = ({ children, col, sortCol, sortDir, onSort, className = '' }) => (
  <th
    className={`text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3 cursor-pointer select-none hover:text-gray-700 ${className}`}
    onClick={() => onSort(col)}
  >
    {children}
    <SortIcon col={col} sortCol={sortCol} sortDir={sortDir} />
  </th>
);

export default function ClinicaFinanceiroPage() {
  const [mes, setMes] = useState(() => new Date().toISOString().slice(0, 7));
  const [tipo, setTipo] = useState('pilates');
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resumo, setResumo] = useState(null);
  const [editandoId, setEditandoId] = useState(null);
  const [novoValor, setNovoValor] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [despesas, setDespesas] = useState(null);
  const [showDespesasModal, setShowDespesasModal] = useState(false);
  const [novaDespesa, setNovaDespesa] = useState('');
  const [valorNovaDespesa, setValorNovaDespesa] = useState('');
  const [novaRecorrente, setNovaRecorrente] = useState(false);
  const [editandoDespesa, setEditandoDespesa] = useState(null);
  const [savingDespesa, setSavingDespesa] = useState(false);

  // Ordenação
  const [sortCol, setSortCol] = useState('nome');
  const [sortDir, setSortDir] = useState('asc');

  // Filtro de fisioterapeuta
  const [filtroFisio, setFiltroFisio] = useState('todos');

  // Painel de filtros
  const [showFiltros, setShowFiltros] = useState(false);
  const [filtroNome, setFiltroNome] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('todos');

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const sortItens = (itens = []) => {
    return [...itens].sort((a, b) => {
      let va, vb;
      if (sortCol === 'nome') { va = a.nome || ''; vb = b.nome || ''; return sortDir === 'asc' ? va.localeCompare(vb, 'pt-BR') : vb.localeCompare(va, 'pt-BR'); }
      if (sortCol === 'mensalidade') { va = a.mensalidade || 0; vb = b.mensalidade || 0; }
      if (sortCol === 'vencimento') { va = a.diaVencimento || 0; vb = b.diaVencimento || 0; }
      if (sortCol === 'status') { va = a.pagamento?.pago ? 1 : 0; vb = b.pagamento?.pago ? 1 : 0; }
      if (sortCol === 'repasse') { va = a.repassePrevisto || 0; vb = b.repassePrevisto || 0; }
      // fisioterapia
      if (sortCol === 'sessoes') { va = a.sessoesRealizadas || 0; vb = b.sessoesRealizadas || 0; }
      if (sortCol === 'valor') { va = a.receitaRealizada || 0; vb = b.receitaRealizada || 0; }
      if (sortCol === 'statusFisio') { const order = { pago: 0, parcial: 1, pendente: 2 }; va = order[a.status] ?? 3; vb = order[b.status] ?? 3; }
      return sortDir === 'asc' ? va - vb : vb - va;
    });
  };

  const fetchDados = useCallback(async () => {
    setLoading(true);
    try {
      if (tipo === 'resumo') {
        const [pilates, fisio] = await Promise.all([
          api.get(`/clinica/financeiro?mes=${mes}&tipo=pilates`),
          api.get(`/clinica/financeiro?mes=${mes}&tipo=fisioterapia`),
        ]);
        const p = pilates.data.data;
        const f = fisio.data.data;
        setResumo({
          previsto: (p.previsto || 0) + (f.previsto || 0),
          recebido: (p.recebido || 0) + (f.recebido || 0),
          pendente: (p.pendente || 0) + (f.pendente || 0),
          totalRepasse: (p.totalRepasse || 0) + (f.totalRepasse || 0),
          pilates: p,
          fisioterapia: f,
        });
        setDados(null);
      } else {
        const { data } = await api.get(`/clinica/financeiro?mes=${mes}&tipo=${tipo}`);
        setDados(data.data);
        setResumo(null);
      }
    } catch { toast.error('Erro ao carregar financeiro'); }
    finally { setLoading(false); }
  }, [mes, tipo]);

  const fetchDespesas = useCallback(async () => {
    if (tipo !== 'resumo') return;
    try {
      const { data } = await api.get(`/clinica/despesas?mes=${mes}`);
      setDespesas(data.data);
    } catch {
      setDespesas({ itensFixos: [], outras: [], totalDespesas: 0 });
    }
  }, [mes, tipo]);

  useEffect(() => { fetchDespesas(); }, [fetchDespesas]);
  useEffect(() => { fetchDados(); }, [fetchDados]);

  const handlePagar = async (id) => {
    try {
      await api.post(`/clinica/financeiro/${id}/pagar`, { mes });
      toast.success('Pagamento confirmado!');
      fetchDados();
    } catch { toast.error('Erro ao confirmar'); }
  };

  const handleEstornar = async (id) => {
    try {
      await api.post(`/clinica/financeiro/${id}/estornar`, { mes });
      toast.success('Estornado.');
      fetchDados();
    } catch { toast.error('Erro ao estornar'); }
  };

  const handleSalvarRecebidoFisio = async (id) => {
    const valor = parseFloat(novoValor);
    if (isNaN(valor) || valor < 0) return toast.error('Valor inválido');
    setSalvando(true);
    try {
      const resp = await api.patch(`/clinica/financeiro/${id}/recebido-fisio`, { valorRecebido: valor, mes });
      const updated = resp.data?.data;
      toast.success('Valor atualizado!');
      setEditandoId(null);
      if (updated) {
        setDados(prev => ({
          ...prev,
          itens: (prev.itens || []).map(i => i._id === id ? { ...i, ...updated } : i),
          recebido: (prev.itens || []).reduce((sum, i) => sum + (i._id === id ? (updated.receitaRealizada || 0) : (i.receitaRealizada || 0)), 0),
        }));
      }
      fetchDados();
    } catch { toast.error('Erro ao salvar'); }
    finally { setSalvando(false); }
  };

  const handleAdicionarDespesa = async () => {
    if (!novaDespesa.trim()) return toast.error('Informe o nome da despesa');
    const valor = parseFloat(valorNovaDespesa) || 0;
    setSavingDespesa(true);
    try {
      await api.post('/clinica/despesas', { nome: novaDespesa, valor, mes, recorrente: novaRecorrente });
      setNovaDespesa('');
      setValorNovaDespesa('');
      setNovaRecorrente(false);
      fetchDespesas();
      toast.success('Despesa adicionada!');
    } catch { toast.error('Erro ao adicionar'); }
    finally { setSavingDespesa(false); }
  };

  const handleExcluirDespesa = async (id) => {
    try {
      await api.delete(`/clinica/despesas/${id}`);
      fetchDespesas();
      toast.success('Removida.');
    } catch { toast.error('Erro ao remover'); }
  };

  const handleGerarRecibo = async (id, nome) => {
    try {
      const resp = await api.get(`/clinica/financeiro/${id}/recibo?mes=${mes}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `recibo-${nome.replace(/\s+/g, '-')}-${mes}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Não foi possível gerar o recibo. Verifique se há pagamento confirmado neste mês.');
    }
  };

  const temRepasse = dados?.itens?.some(i => i.percentualRepasse);

  // Lista de fisios únicos presentes nos dados atuais
  const fisiosDisponiveis = [...new Map(
    (dados?.itens || [])
      .filter(i => i.fisioterapeuta)
      .map(i => [i.fisioterapeuta, i.fisioterapeuta])
  ).values()];

  const itensFiltrados = (dados?.itens || []).filter(item => {
    if (filtroFisio !== 'todos' && item.fisioterapeuta !== filtroFisio) return false;
    if (filtroNome.trim() && !item.nome.toLowerCase().includes(filtroNome.toLowerCase())) return false;
    if (filtroStatus !== 'todos') {
      if (tipo === 'pilates') {
        const pago = item.pagamento?.pago;
        if (filtroStatus === 'pago' && !pago) return false;
        if (filtroStatus === 'pendente' && pago) return false;
      } else {
        if (filtroStatus !== item.status) return false;
      }
    }
    return true;
  });

  const filtrosAtivos = [
    filtroNome.trim() !== '',
    filtroStatus !== 'todos',
    filtroFisio !== 'todos',
  ].filter(Boolean).length;

  const itensSorted = sortItens(itensFiltrados);

  // Totais recalculados com base no filtro de fisio
  const totaisExibidos = filtroFisio === 'todos' ? {
    previsto: dados?.previsto,
    recebido: dados?.recebido,
    pendente: dados?.pendente,
    totalRepasse: dados?.totalRepasse,
  } : itensFiltrados.reduce((acc, item) => {
    // pilates
    if (tipo === 'pilates') {
      acc.previsto += item.mensalidade || 0;
      acc.recebido += item.pagamento?.pago ? (item.mensalidade || 0) : 0;
      acc.pendente += item.pagamento?.pago ? 0 : (item.mensalidade || 0);
      acc.totalRepasse += item.repassePrevisto || 0;
    } else {
      // fisioterapia
      acc.previsto += item.receitaPrevista || 0;
      acc.recebido += item.receitaRealizada || 0;
      acc.pendente += item.saldoPendente || 0;
      acc.totalRepasse += item.valorRepasse || 0;
    }
    return acc;
  }, { previsto: 0, recebido: 0, pendente: 0, totalRepasse: 0 });

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Financeiro</h1>
          <p className="text-gray-500 mt-1">Controle financeiro da clínica</p>
        </div>
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2 shadow-sm">
          <button onClick={() => setMes(m => moveMes(m, -1))} className="text-gray-500 hover:text-gray-700 p-0.5"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm font-medium text-gray-700 w-24 text-center">{formatMesLabel(mes)}</span>
          <button onClick={() => setMes(m => moveMes(m, 1))} className="text-gray-500 hover:text-gray-700 p-0.5"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit mb-6">
        {[{ key: 'pilates', label: 'Pilates (Mensalidade)' }, { key: 'fisioterapia', label: 'Fisioterapia (Sessões)' }, { key: 'resumo', label: 'Resumo Total' }].map(({ key, label }) => (
          <button key={key} onClick={() => { setTipo(key); setFiltroFisio('todos'); setFiltroNome(''); setFiltroStatus('todos'); setShowFiltros(false); }}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${tipo === key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Botão e modal de filtros */}
      {tipo !== 'resumo' && (
        <div className="mb-4 flex items-center gap-3">
          <button
            onClick={() => setShowFiltros(true)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition-colors ${filtrosAtivos > 0 ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
            <Filter className="w-4 h-4" />
            Filtros
            {filtrosAtivos > 0 && (
              <span className="bg-white text-blue-600 text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {filtrosAtivos}
              </span>
            )}
          </button>
          {filtrosAtivos > 0 && (
            <button
              onClick={() => { setFiltroNome(''); setFiltroStatus('todos'); setFiltroFisio('todos'); }}
              className="text-xs text-gray-400 hover:text-red-500 transition-colors">
              Limpar filtros
            </button>
          )}
        </div>
      )}

      {/* Modal de filtros */}
      {showFiltros && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h3 className="text-base font-bold text-gray-900">Filtros</h3>
              <button onClick={() => setShowFiltros(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
            </div>
            <div className="p-5 space-y-5">
              {/* Nome */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">Nome do paciente</label>
                <input
                  type="text"
                  placeholder="Buscar por nome..."
                  value={filtroNome}
                  onChange={e => setFiltroNome(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 outline-none"
                />
              </div>

              {/* Status */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">Status</label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { key: 'todos', label: 'Todos' },
                    { key: 'pago', label: 'Pago' },
                    { key: 'pendente', label: 'Pendente' },
                    ...(tipo === 'fisioterapia' ? [{ key: 'parcial', label: 'Parcial' }] : []),
                  ].map(s => (
                    <button key={s.key} onClick={() => setFiltroStatus(s.key)}
                      className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${filtroStatus === s.key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Fisioterapeuta — só na aba fisioterapia */}
              {tipo === 'fisioterapia' && fisiosDisponiveis.length > 0 && (
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">Fisioterapeuta</label>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => setFiltroFisio('todos')}
                      className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${filtroFisio === 'todos' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                      Todos
                    </button>
                    {fisiosDisponiveis.map(nome => (
                      <button key={nome} onClick={() => setFiltroFisio(nome)}
                        className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${filtroFisio === nome ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                        {nome}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-3 p-5 border-t border-gray-100">
              <button
                onClick={() => { setFiltroNome(''); setFiltroStatus('todos'); setFiltroFisio('todos'); }}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors">
                Limpar
              </button>
              <button
                onClick={() => setShowFiltros(false)}
                className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-xl transition-colors">
                Aplicar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { icon: <TrendingUp className="w-6 h-6 text-blue-600" />, color: 'bg-blue-50', label: 'Previsto', value: formatBRL(totaisExibidos?.previsto) },
          { icon: <DollarSign className="w-6 h-6 text-green-600" />, color: 'bg-green-50', label: 'Recebido', value: formatBRL(totaisExibidos?.recebido) },
          { icon: <TrendingDown className="w-6 h-6 text-red-500" />, color: 'bg-red-50', label: 'Pendente', value: formatBRL(totaisExibidos?.pendente) },
          { icon: <ArrowLeftRight className="w-6 h-6 text-orange-500" />, color: 'bg-orange-50', label: 'Total Repasse Fisios', value: formatBRL(totaisExibidos?.totalRepasse) },
        ].map((card) => (
          <div key={card.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${card.color}`}>{card.icon}</div>
            <div>
              <p className="text-xl font-bold text-gray-900">{card.value}</p>
              <p className="text-xs text-gray-500">{card.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Resumo */}
      {tipo === 'resumo' && resumo && !loading && (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Consolidado — {formatMesLabel(mes)}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { label: 'Pilates — Previsto', value: resumo.pilates?.previsto, color: 'text-blue-600' },
                { label: 'Pilates — Recebido', value: resumo.pilates?.recebido, color: 'text-green-600' },
                { label: 'Fisioterapia — Previsto', value: resumo.fisioterapia?.previsto, color: 'text-blue-600' },
                { label: 'Fisioterapia — Recebido', value: resumo.fisioterapia?.recebido, color: 'text-green-600' },
                { label: 'Total Repasse Fisios', value: resumo.totalRepasse, color: 'text-orange-500' },
                { label: 'Total Pendente', value: resumo.pendente, color: 'text-red-500' },
              ].map(item => (
                <div key={item.label} className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-xl">
                  <span className="text-sm text-gray-600">{item.label}</span>
                  <span className={`text-sm font-bold ${item.color}`}>{formatBRL(item.value)}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
              <span className="text-base font-semibold text-gray-900">Total Geral Previsto</span>
              <span className="text-xl font-bold text-gray-900">{formatBRL(resumo.previsto)}</span>
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className="text-base font-semibold text-gray-900">Total Geral Recebido</span>
              <span className="text-xl font-bold text-green-600">{formatBRL(resumo.recebido)}</span>
            </div>
            <div className="flex items-center justify-between mt-1 pt-3 border-t border-dashed border-gray-200">
              <div>
                <span className="text-base font-semibold text-gray-900">Líquido da Clínica</span>
                <p className="text-xs text-gray-400">Recebido − Repasses ({formatBRL(resumo.totalRepasse)})</p>
              </div>
              <span className="text-xl font-bold text-blue-600">{formatBRL((resumo.recebido || 0) - (resumo.totalRepasse || 0))}</span>
            </div>
            <div className="flex items-center justify-between mt-1 pt-3 border-t-2 border-gray-200">
              <div>
                <span className="text-base font-semibold text-gray-900">Saldo Final do Mês</span>
                <p className="text-xs text-gray-400">Líquido − Despesas ({formatBRL(despesas?.totalDespesas || 0)})</p>
              </div>
              <span className={`text-xl font-bold ${((resumo.recebido||0)-(resumo.totalRepasse||0)-(despesas?.totalDespesas||0))>=0?'text-green-600':'text-red-600'}`}>
                {formatBRL((resumo.recebido||0)-(resumo.totalRepasse||0)-(despesas?.totalDespesas||0))}
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between bg-white rounded-2xl border border-gray-100 shadow-sm px-6 py-4">
            <div>
              <p className="text-sm font-semibold text-gray-900">Despesas do mês</p>
              <p className="text-xs text-gray-500">
                Fixas: <span className="font-medium text-gray-700">{formatBRL((despesas?.fixas||[]).reduce((s,d)=>s+(d.valor||0),0))}</span>
                {' · '}
                Variáveis: <span className="font-medium text-gray-700">{formatBRL((despesas?.variaveis||[]).reduce((s,d)=>s+(d.valor||0),0))}</span>
              </p>
            </div>
            <button onClick={() => { fetchDespesas(); setShowDespesasModal(true); }}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-xl transition-colors">
              Gerenciar Despesas
            </button>
          </div>
        </div>
      )}

      {/* Modal despesas */}
      {showDespesasModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg flex flex-col max-h-[85vh]">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Despesas — {formatMesLabel(mes)}</h3>
                <p className="text-xs text-gray-400">Total: <span className="font-medium text-red-600">{formatBRL(despesas?.totalDespesas || 0)}</span></p>
              </div>
              <button onClick={() => setShowDespesasModal(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
            </div>
            <div className="overflow-y-auto flex-1 p-5 space-y-5">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Contas Fixas (recorrentes)</p>
                {(despesas?.fixas || []).length === 0 && <p className="text-xs text-gray-400">Nenhuma conta fixa cadastrada.</p>}
                <div className="space-y-2">
                  {(despesas?.fixas || []).map(item => (
                    <div key={item._id} className="flex items-center justify-between px-4 py-2.5 bg-blue-50 rounded-xl">
                      <div className="flex items-center gap-2">
                        <span className="text-blue-400">🔄</span>
                        {editandoDespesa === item._id ? (
                          <input type="text" defaultValue={item.nome}
                            onBlur={e => api.patch(`/clinica/despesas/${item._id}`, { nome: e.target.value }).then(fetchDespesas)}
                            className="text-sm border border-gray-200 rounded px-2 py-0.5 w-32 outline-none" />
                        ) : (
                          <p className="text-sm font-medium text-gray-900">{item.nome}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {editandoDespesa === item._id ? (
                          <>
                            <input type="number" defaultValue={item.valor} id={`val-${item._id}`}
                              className="w-20 border border-gray-200 rounded px-2 py-0.5 text-sm outline-none" />
                            <button onClick={async () => {
                              const val = document.getElementById(`val-${item._id}`).value;
                              await api.patch(`/clinica/despesas/${item._id}`, { valor: val });
                              fetchDespesas(); setEditandoDespesa(null);
                            }} className="px-2 py-1 bg-blue-600 text-white text-xs rounded-lg">✓</button>
                            <button onClick={() => setEditandoDespesa(null)} className="text-xs text-gray-400">✕</button>
                          </>
                        ) : (
                          <>
                            <span className="text-sm font-medium text-gray-800">{formatBRL(item.valor)}</span>
                            <button onClick={() => setEditandoDespesa(item._id)} className="text-gray-400 hover:text-blue-600 text-xs">✏️</button>
                            <button onClick={() => handleExcluirDespesa(item._id)} className="text-gray-400 hover:text-red-500 text-xs">✕</button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Contas Variáveis (só este mês)</p>
                {(despesas?.variaveis || []).length === 0 && <p className="text-xs text-gray-400">Nenhuma conta variável cadastrada.</p>}
                <div className="space-y-2">
                  {(despesas?.variaveis || []).map(item => (
                    <div key={item._id} className="flex items-center justify-between px-4 py-2.5 bg-gray-50 rounded-xl">
                      <p className="text-sm font-medium text-gray-900">{item.nome}</p>
                      <div className="flex items-center gap-2">
                        {editandoDespesa === item._id ? (
                          <>
                            <input type="number" defaultValue={item.valor} id={`val-${item._id}`}
                              className="w-20 border border-gray-200 rounded px-2 py-0.5 text-sm outline-none" />
                            <button onClick={async () => {
                              const val = document.getElementById(`val-${item._id}`).value;
                              await api.patch(`/clinica/despesas/${item._id}`, { valor: val });
                              fetchDespesas(); setEditandoDespesa(null);
                            }} className="px-2 py-1 bg-blue-600 text-white text-xs rounded-lg">✓</button>
                            <button onClick={() => setEditandoDespesa(null)} className="text-xs text-gray-400">✕</button>
                          </>
                        ) : (
                          <>
                            <span className="text-sm font-medium text-gray-800">{formatBRL(item.valor)}</span>
                            <button onClick={() => setEditandoDespesa(item._id)} className="text-gray-400 hover:text-blue-600 text-xs">✏️</button>
                            <button onClick={() => handleExcluirDespesa(item._id)} className="text-gray-400 hover:text-red-500 text-xs">✕</button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Adicionar despesa</p>
                <div className="space-y-3">
                  <input type="text" placeholder="Nome da despesa (ex: Aluguel, Água, Material...)"
                    value={novaDespesa} onChange={e => setNovaDespesa(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 outline-none" />
                  <input type="number" placeholder="Valor (R$)"
                    value={valorNovaDespesa} onChange={e => setValorNovaDespesa(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 outline-none" />
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={novaRecorrente} onChange={e => setNovaRecorrente(e.target.checked)}
                      className="w-4 h-4 rounded accent-blue-600" />
                    <span className="text-sm text-gray-700">Conta fixa — replica para os próximos meses</span>
                  </label>
                  <button onClick={handleAdicionarDespesa} disabled={savingDespesa}
                    className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white text-sm font-medium rounded-xl transition-colors">
                    {savingDespesa ? 'Salvando...' : '+ Adicionar Despesa'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabela */}
      {tipo !== 'resumo' && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
          ) : !dados?.itens?.length ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-3"><DollarSign className="w-7 h-7 text-gray-400" /></div>
              <p className="text-sm text-gray-500">Nenhum paciente encontrado para este período.</p>
            </div>
          ) : tipo === 'pilates' ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-gray-100 bg-gray-50">
                  <tr>
                    <Th col="nome" sortCol={sortCol} sortDir={sortDir} onSort={handleSort}>Paciente</Th>
                    <Th col="mensalidade" sortCol={sortCol} sortDir={sortDir} onSort={handleSort}>Mensalidade</Th>
                    <Th col="vencimento" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} className="hidden sm:table-cell">Vencimento</Th>
                    {temRepasse && <Th col="repasse" sortCol={sortCol} sortDir={sortDir} onSort={handleSort}>Repasse Fisio</Th>}
                    <Th col="status" sortCol={sortCol} sortDir={sortDir} onSort={handleSort}>Status</Th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3 hidden sm:table-cell">Data Pagamento</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3">Ação</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {itensSorted.map(item => (
                    <tr key={item._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-4">
                        <p className="text-sm font-medium text-gray-900">{item.nome}</p>
                        {item.percentualRepasse && <p className="text-xs text-gray-400">{item.percentualRepasse}% repasse</p>}
                      </td>
                      <td className="px-5 py-4 text-sm font-medium text-gray-800">{formatBRL(item.mensalidade)}</td>
                      <td className="px-5 py-4 text-sm text-gray-500 hidden sm:table-cell">{item.diaVencimento ? `Dia ${item.diaVencimento}` : '—'}</td>
                      {temRepasse && (
                        <td className="px-5 py-4">
                          {item.percentualRepasse ? (
                            <div>
                              <p className="text-sm font-medium text-orange-600">{formatBRL(item.repassePrevisto)}</p>
                              <p className="text-xs text-gray-400">{item.percentualRepasse}% · {formatBRL(item.valorSessaoFisio)}/sessão</p>
                            </div>
                          ) : <span className="text-sm text-gray-400">—</span>}
                        </td>
                      )}
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${item.pagamento?.pago ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                          {item.pagamento?.pago ? 'Pago' : 'Pendente'}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-500 hidden sm:table-cell">
                        {item.pagamento?.dataPagamento ? new Date(item.pagamento.dataPagamento).toLocaleDateString('pt-BR') : '—'}
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          {item.pagamento?.pago
                            ? <button onClick={() => handleEstornar(item._id)} className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">Estornar</button>
                            : <button onClick={() => handlePagar(item._id)} className="px-3 py-1.5 text-xs font-medium text-white bg-green-500 hover:bg-green-600 rounded-lg transition-colors">Confirmar</button>
                          }
                          {item.pagamento?.pago && (
                            <button onClick={() => handleGerarRecibo(item._id, item.nome)}
                              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium" title="Gerar recibo">
                              <FileText className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-gray-100 bg-gray-50">
                  <tr>
                    <Th col="nome" sortCol={sortCol} sortDir={sortDir} onSort={handleSort}>Paciente</Th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3 hidden md:table-cell">Fisioterapeuta</th>
                    <Th col="sessoes" sortCol={sortCol} sortDir={sortDir} onSort={handleSort}>Sessões</Th>
                    <Th col="valor" sortCol={sortCol} sortDir={sortDir} onSort={handleSort}>Valor Clínica</Th>
                    {temRepasse && <Th col="repasse" sortCol={sortCol} sortDir={sortDir} onSort={handleSort}>Repasse Fisio</Th>}
                    <Th col="statusFisio" sortCol={sortCol} sortDir={sortDir} onSort={handleSort}>Status</Th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {itensSorted.map(item => (
                    <tr key={item._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-4">
                        <p className="text-sm font-medium text-gray-900">{item.nome}</p>
                        <p className="text-xs text-gray-400">{item.tipoCobranca === 'pacote' ? 'Pacote' : 'Por Sessão'}</p>
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-600 hidden md:table-cell">{item.fisioterapeuta}</td>
                      <td className="px-5 py-4 text-sm text-gray-700">
                        <span className="font-medium">{item.sessoesRealizadas}</span>
                        <span className="text-gray-400"> / {item.sessoesAgendadas || 0} agendadas</span>
                        <p className="text-xs text-gray-400">{item.totalSessoes} total</p>
                      </td>
                      <td className="px-5 py-4">
                        {item.tipoCobranca === 'pacote' ? (
                          editandoId === item._id ? (
                            <div className="flex items-center gap-2">
                              <input type="number" value={novoValor} min={0} step="0.01"
                                onChange={e => setNovoValor(e.target.value)}
                                className="w-28 border border-gray-200 rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                autoFocus />
                              <button onClick={() => handleSalvarRecebidoFisio(item._id)} disabled={salvando}
                                className="w-7 h-7 bg-green-500 hover:bg-green-600 text-white rounded-lg flex items-center justify-center">
                                <Check className="w-4 h-4" />
                              </button>
                              <button onClick={() => setEditandoId(null)} className="text-xs text-gray-400 hover:text-gray-600">✕</button>
                            </div>
                          ) : (
                            <button onClick={() => { setEditandoId(item._id); setNovoValor(String(item.receitaRealizada || 0)); }}
                              className="text-sm font-medium text-gray-800 hover:text-blue-600 flex items-center gap-1 group" title="Clique para editar">
                              <div>
                                <p>{formatBRL(item.receitaRealizada)}</p>
                                <p className="text-xs text-gray-400">{formatBRL(item.valorSessaoClinica)}/sessão</p>
                              </div>
                              <ChevronDown className="w-3 h-3 text-gray-400 group-hover:text-blue-400" />
                            </button>
                          )
                        ) : (
                          <div>
                            <p className="text-sm font-medium text-gray-800">{formatBRL(item.receitaRealizada)}</p>
                            <p className="text-xs text-gray-400">{formatBRL(item.valorSessaoClinica)}/sessão</p>
                          </div>
                        )}
                      </td>
                      {temRepasse && (
                        <td className="px-5 py-4">
                          {item.percentualRepasse ? (
                            <div>
                              <p className="text-sm font-medium text-orange-600">{formatBRL(item.valorRepasse)}</p>
                              <p className="text-xs text-gray-400">{item.percentualRepasse}% · {formatBRL(item.valorSessaoFisio)}/sessão</p>
                            </div>
                          ) : <span className="text-sm text-gray-400">—</span>}
                        </td>
                      )}
                      <td className="px-5 py-4"><StatusBadge status={item.status} /></td>
                      <td className="px-5 py-4">
                        {item.status === 'pago' && (
                          <button onClick={() => handleGerarRecibo(item._id, item.nome)}
                            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium" title="Gerar recibo">
                            <FileText className="w-3.5 h-3.5" /> Recibo
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}